import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getFluxxUserId } from "@/services/postLoginUser";

import type { EffectiveRole } from "../auth/types";
import type { RoleFeature } from "../services/roles";
import { getUserDetails } from "../services/roles";

const STORAGE_KEY = "selectedRoles";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

type RoleFeaturesGroup = {
  role_code: string;
  features: RoleFeature[];
};

export type Timezone = {
  id?: number;
  key: string;
  label: string;
  country: string;
  timezone: string;
  officeStartHour: number;
  officeEndHour: number;
  navbar_timezone_id?: number;
};

type QuickLink = {
  id: string;
  name: string;
  url: string;
  display_order: number;
};

type RoleState = {
  roles: EffectiveRole[];
  roleFeatures: RoleFeaturesGroup[];
  selectedRole: string | null;
  fluxxUserId: string | null;
  features: RoleFeature[];
  timezones: Timezone[];
  language: string;
  quickLinks: QuickLink[];
  showTour: boolean;
  tourReason?: string;
  status: RequestStatus;
  error: string | null;
};

const initialState: RoleState = {
  roles: [],
  roleFeatures: [],
  selectedRole: null,
  fluxxUserId: null,
  features: [],
  language: "",
  timezones: [],
  quickLinks: [],
  showTour: false,
  tourReason: undefined,
  status: "idle",
  error: null,
};

export const fetchUserDetailsByEmail = createAsyncThunk<
  {
    roles: EffectiveRole[];
    roleFeatures: RoleFeaturesGroup[];
    timezones: Timezone[];
    quickLinks: QuickLink[];
    showTour: boolean;
    reason?: string;
    language: string;
  },
  string,
  { state: any }
>(
  "role/fetchUserDetailsByEmail",
  async (email) => {
    const { data } = await getUserDetails(email);

    return {
      roles: Array.isArray(data?.roleCodes) ? data.roleCodes : [],
      roleFeatures: Array.isArray(data?.roleFeatures) ? data.roleFeatures : [],
      timezones: Array.isArray(data?.timezones) ? data.timezones : [],
      quickLinks: Array.isArray(data?.quickLinks) ? data.quickLinks : [],
      showTour: Boolean(data?.showTour),
      reason: data?.reason,
      language: data?.language,
    };
  },
  {
    condition: (email, { getState }) => {
      const state = getState();

      if (state.role.status !== "idle") return false;
      if (!email) return false;

      return true;
    },
  }
);

export const getFluxxId: any = createAsyncThunk<string, string>(
  "role/getFluxxUserId",
  async (userId: string) => {
    const response: any = await getFluxxUserId(userId);
    return response?.userId || "";
  }
);

export const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    setRole(state, action: PayloadAction<EffectiveRole>) {
      state.roles = [action.payload];
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.roles));
      } catch {
        /* ignore */
      }
    },

    setRoles(state, action: PayloadAction<EffectiveRole[]>) {
      state.roles = action.payload;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.roles));
      } catch {
        /* ignore */
      }
    },

    setShowTour(state, action: PayloadAction<boolean>) {
      state.showTour = action.payload;
    },

    setSelectedRole(state, action: PayloadAction<string>) {
      state.selectedRole = action.payload;
      const selectedGroup = state.roleFeatures.find((r) => r.role_code === action.payload);
      state.features = selectedGroup?.features ?? [];
    },

    setTimezones(state, action: PayloadAction<Timezone[]>) {
      state.timezones = action.payload;
    },

    resetRoleState(state) {
      state.roles = [];
      state.features = [];
      state.roleFeatures = [];
      state.selectedRole = null;
      state.timezones = [];
      state.language = "";
      state.quickLinks = [];
      state.showTour = false;
      state.tourReason = undefined;
      state.status = "idle";
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailsByEmail.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })

      .addCase(fetchUserDetailsByEmail.fulfilled, (state, action) => {
        state.status = "succeeded";

        state.roles = action.payload.roles;
        state.roleFeatures = action.payload.roleFeatures;
        state.timezones = action.payload.timezones;

        const defaultRole = action.payload.roles[0] ?? null;
        state.selectedRole = defaultRole;

        const selectedGroup = state.roleFeatures.find((r) => r.role_code === defaultRole);
        state.features = selectedGroup?.features ?? [];

        state.language = action.payload.language || "English";
        state.quickLinks = action.payload.quickLinks;
        state.showTour = action.payload.showTour;
        state.tourReason = action.payload.reason;
      })

      .addCase(fetchUserDetailsByEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load user profile";
        state.features = [];
        state.quickLinks = [];
      })

      .addCase(getFluxxId.fulfilled, (state, action) => {
        state.fluxxUserId = action.payload;
      });
  },
});

export const { setRole, setRoles, setSelectedRole, resetRoleState, setShowTour, setTimezones } =
  roleSlice.actions;

export const selectFluxxUserId = (s: any): string | null => s.role.fluxxUserId ?? null;

export const selectRoles = (s: any): EffectiveRole[] => s.role.roles;

export const selectRole = (s: any): EffectiveRole[] => {
  const roles = selectRoles(s);
  return roles.length > 0 ? roles : ["Partner"];
};

export const selectFeatures = (s: any): RoleFeature[] => s.role.features;

export const selectQuickLinks = (s: any): QuickLink[] => s.role.quickLinks;

export const selectLanguage = (s: any): string => s.role.language;

export const selectShowTour = (s: any): boolean => s.role.showTour;

export const selectTourReason = (s: any): string | undefined => s.role.tourReason;

export const selectFeaturesStatus = (s: any): RequestStatus => s.role.status;

export const selectFeaturesLoading = (s: any): boolean => s.role.status === "loading";

export const selectFeaturesError = (s: any): string | null => s.role.error;

export const selectSelectedRole = (s: any): string | null => s.role.selectedRole;

export const selectTimezones = (s: any): Timezone[] => s.role.timezones;

export const selectHasFeature = (state: any, featureCode: string): boolean => {
  const normalized = featureCode?.toLowerCase();
  return state.role.features.some((f: RoleFeature) => f.feature_code?.toLowerCase() === normalized);
};

export const selectHasFeatureByPath = (state: any, routePath: string): boolean => {
  const normalized = routePath?.toLowerCase();
  return state.role.features.some(
    (f: RoleFeature) => f.route_path?.toLowerCase() === normalized && f.access
  );
};

export const selectDashboardFeatures = (state: any): RoleFeature[] =>
  state.role.features.filter(
    (f: RoleFeature) =>
      f.domain === "dashboard" &&
      f.feature_code !== "dashboard" &&
      f.feature_code !== "dashboard.settings" &&
      f.access
  );

export const selectDashboardDefaultWidgets = (state: any) =>
  selectDashboardFeatures(state).filter((f) => f.access_type === "DEFAULT");

export const selectDashboardOptionalWidgets = (state: any) =>
  selectDashboardFeatures(state).filter((f) => f.access_type === "OPTIONAL");

export const selectDashboardAllWidgets = (state: any) =>
  selectDashboardFeatures(state).filter(
    (f) => f.access_type === "DEFAULT" || f.access_type === "OPTIONAL"
  );

export default roleSlice.reducer;
